import { css } from '@emotion/react';
import { Box } from '@inflearn/ds-react-next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { eventNames } from '../../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../../utils/GTM/useTrackMixpanelEvent';
import { HeroStage } from './hero/HeroStage';
import { useAiInterviewHeroUserState } from './hooks/useAiInterviewHeroUserState';
import { Faq } from './landing/Faq';
import { FloatingBar } from './landing/FloatingBar';
import { FollowUpChat } from './landing/FollowUpChat';
import { HowItWorks } from './landing/HowItWorks';
import { PastQuestions } from './landing/PastQuestions';
import { QuickStart } from './landing/QuickStart';
import { ReportPreview } from './landing/ReportPreview';
import { Reviews } from './landing/Reviews';
import { useReveal } from './landing/useReveal';
import { useTrackScrollDepth } from './landing/useTrackScrollDepth';

const LANDING_ENTRY_POINTS = [
  'header_service_menu',
  'header_profile_menu',
  'profile',
  'my_courses',
  'community',
  'mentoring_list',
  'main_banner',
  'my_ai_interviews',
  'threads',
  'direct',
] as const;

type LandingEntryPoint = (typeof LANDING_ENTRY_POINTS)[number];

function toLandingEntryPoint(from: unknown): LandingEntryPoint {
  return LANDING_ENTRY_POINTS.includes(from as LandingEntryPoint)
    ? (from as LandingEntryPoint)
    : 'direct';
}

/**
 * 원티드형 스크롤:
 * 히어로 sticky 구간에서 스크롤이 시퀀스(카드 머지 + 카피 등장)를 구동하고,
 * 인계 이후에 아래 섹션이 문서 흐름으로 이어진다.
 */
export const AiInterviewsPageItem = () => {
  const { userState } = useAiInterviewHeroUserState();
  const router = useRouter();
  const { trackMixpanelEvent, isReady } = useTrackMixpanelEvent();
  useReveal();
  useTrackScrollDepth();

  useEffect(
    function trackViewMockInterview() {
      if (!router.isReady || !isReady) {
        return;
      }

      trackMixpanelEvent(
        eventNames.VIEW_MOCK_INTERVIEW,
        { entry_point: toLandingEntryPoint(router.query.from) },
        { once: true },
      );
    },
    [router.isReady, isReady, router.query.from, trackMixpanelEvent],
  );

  return (
    <Box css={styles.page}>
      <HeroStage userState={userState} />
      <Box css={styles.content}>
        <PastQuestions />
        <QuickStart />
        <FollowUpChat />
        <ReportPreview />
        <HowItWorks />
        <Reviews />
        <Faq />
      </Box>
      <FloatingBar />
    </Box>
  );
};

const styles = {
  page: css({
    position: 'relative',
    background: '#0E1013',
  }),
  content: css({
    position: 'relative',
    zIndex: 1,
  }),
};
